import { motion } from "framer-motion";
import { X } from "lucide-react";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Modal from "../components/Modal";
import { InfoLine, infoLines } from "../data/info-lines";
import { pageMetaConfigs } from "../data/pageMetaConfigs";
import { useMetaTags } from "../hooks/useMetaTags";
import { Review } from "../types";
// import { Review, BotStats } from "../types";

const Features = lazy(() => import("../components/Features"));

const Homepage: React.FC = () => {
  // Use meta tags hook
  useMetaTags(pageMetaConfigs.home);

  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [activeInfoLines, setActiveInfoLines] = useState<InfoLine[]>([]);
 // const [stats, setStats] = useState<BotStats | null>(null);
  // const [isLargeScreen, setIsLargeScreen] = useState(() => window.innerWidth > 1280);

  const reviews = useMemo(
    () => [
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/kiso.webp",
        username: "kiso",
        comment: "The variety is amazing, with a lot of thoughts behind it.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/angel.webp",
        username: "angel",
        comment: "this bot is so silly.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/levi.webp",
        username: "Léví",
        comment: "Worth every cent.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/Ka!.webp",
        username: "Ka!",
        comment: "Exceeded my expectations.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/dj.webp",
        username: "dj",
        comment: "Fantastic bot and support.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/thrivebot.webp",
        username: "thrivebot",
        comment: "I love myself!",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/waturu.webp",
        username: "waturu",
        comment: "Unlimited features!!! 5/5 no drama.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/nephilim.webp",
        username: "nephilim",
        comment:
          "My experience with thrive bot been so exciting. I liked the idea and commands like rob and slot. It has also very nice feature for the RNG story generation. And it's pretty cool.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/哲基尔.webp",
        username: "哲基尔",
        comment:
          "Its convenient to use even for beginners who arent used for it. I kinda like it bcs i can learn how to use it in a short time.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/flulosta.webp",
        username: "flulost",
        comment:
          "Thrivebot has been a fantastic addition to our Discord server It keeps us updated with the weather, helps us remember birthdays, and offers fun games to play. Its super easy to use and really versatile honestly, its a must have for any community hghly recommended.",
      },
      {
        avatar: "https://cdn.thrivebot.in/review-user-avatars/STARBOY.webp",
        username: "STARBOY",
        comment:
          "Ive been using Thrive Bot, and its been a fantastic experience! The bot is incredibly user-friendly, making it easy to navigate and utilize its features. Whether youre a beginner or an experienced user, youll find Thrive Bot intuitive and efficient. It helped me streamline my tasks and manage my time more effectively. Highly recommended for anyone looking for a reliable and straightforward solution!",
      },
      {
        avatar:
          "https://cdn.thrivebot.in/review-user-avatars/uncle_shadew.webp",
        username: "uncle shadew",
        comment:
          "games are really fun to play thing and the interesting part is that this bot can be played like for short time and close , you dont have to give more time on it like other games , Try it if you are a busy person and like playing games.",
      },
    ],
    []
  );

  const handleReviewClick = useCallback(
    (review: Review) => setSelectedReview(review),
    []
  );
  const closeModal = useCallback(() => setSelectedReview(null), []);

  const removeInfoLine = useCallback((id: string) => {
    const dismissalTime = Date.now();
    localStorage.setItem(`dismissed-${id}`, dismissalTime.toString());
    setActiveInfoLines((prev) => prev.filter((line) => line.id !== id));
  }, []);

  const checkActiveInfoLines = useCallback(() => {
    const now = Date.now();
    const active = infoLines.filter((line) => {
      const start = new Date(line.startDate).getTime();
      const end = new Date(line.endDate).getTime();
      const isWithinDateRange = now >= start && now <= end;
      const dismissedTime = localStorage.getItem(`dismissed-${line.id}`);
      return dismissedTime
        ? now - parseInt(dismissedTime) >= 7200000 && isWithinDateRange
        : isWithinDateRange;
    });
    setActiveInfoLines(active);
  }, []);

  // const handleResize = useCallback(() => {
  //   setIsLargeScreen(window.innerWidth > 1280);
  // }, []);

  // Scroll restoration logic
  useEffect(() => {
    const isHomepage = window.location.pathname === "/";
    const isRefresh = performance.navigation.type === 1; // 1 indicates a reload
    const savedPosition = sessionStorage.getItem("homepageScrollPosition");

    // Restore scroll position only on homepage refresh
    const restoreScrollPosition = () => {
      if (isHomepage && isRefresh && savedPosition) {
        window.scrollTo(0, parseInt(savedPosition, 10));
      } else if (isHomepage && !isRefresh) {
        // Reset to top when navigating to homepage from another page
        window.scrollTo(0, 0);
        sessionStorage.removeItem("homepageScrollPosition");
      }
    };

    // Initial restoration check
    restoreScrollPosition();

    // Throttle scroll position saving to improve performance
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const saveScrollPosition = () => {
      if (!isHomepage) return;

      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        sessionStorage.setItem(
          "homepageScrollPosition",
          window.scrollY.toString()
        );
      }, 200);
    };

    // Add scroll listener to save position dynamically
    window.addEventListener("scroll", saveScrollPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", saveScrollPosition);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    checkActiveInfoLines();
    const timer = setInterval(checkActiveInfoLines, 60000);
    return () => clearInterval(timer);
  }, [checkActiveInfoLines]);

  // useEffect(() => {
  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, [handleResize]);

  // useEffect(() => {
  //   const fetchStats = async () => {
  //     try {
  //       const response = await fetch("https://status.thrivebot.in/botstats", {
  //         cache: "no-store",
  //       });
  //       if (!response.ok) throw new Error("Failed to fetch stats");
  //       const data = await response.json();
  //       setStats(data);
  //     } catch (error) {
  //       // Silently fail - stats are non-critical
  //     }
  //   };
  //   fetchStats();
  // }, []);

  // const formatNumber = useCallback((number: number): string => {
  //   const thresholds = [
  //     { limit: 1e12, suffix: "t" },
  //     { limit: 1e9, suffix: "b" },
  //     { limit: 1e6, suffix: "m" },
  //     { limit: 1e3, suffix: "k" },
  //   ];
  //   const match = thresholds.find(({ limit }) => number >= limit);
  //   return match
  //     ? (number / match.limit).toFixed(1) + match.suffix
  //     : number.toString();
  // }, []);

  // const statsItems = useMemo(
  //   () =>
  //     stats
  //       ? [
  //         { title: "Total Users", value: stats.totalUsers },
  //         {
  //           title: "Total Money",
  //           value: stats.totalMoney,
  //           icon: "https://cdn.thrivebot.in/thrivebot/thrivebot_cents.webp",
  //         },
  //         { title: "Banned Users", value: stats.bannedUsers },
  //         { title: "Command Usage", value: stats.totalCommandUsage },
  //       ]
  //       : [],
  //   [stats]
  // );

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100 no-copy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1
            className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 bg-clip-text text-transparent animate-pastel-flow"
            style={{
              backgroundImage:
                "linear-gradient(45deg, hsl(0, 70%, 85%), hsl(360, 70%, 85%))",
              backgroundSize: "200% 200%",
            }}
          >
            Elevate Your Discord Experience
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Your all-in-one solution for server management and engagement
          </p>
        </motion.div>

        <Suspense fallback={<div className="text-center">Loading...</div>}>
          <Features />
        </Suspense>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-2xl mx-auto rounded-lg shadow-md p-6 mt-12 border-gray-200 dark:border-gray-800"
        >
          <blockquote className="text-lg italic text-gray-700 dark:text-gray-300">
            "Welcome to Thrivebot – your dynamic Discord companion!"
          </blockquote>
          <p className="mt-2 text-right text-sm font-semibold text-blue-600 dark:text-blue-400">
            — ocyeanic, Founder
          </p>
        </motion.div>

        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 rounded-lg p-6 shadow-lg bg-gradient-to-br bg-white dark:bg-gray-900"
        >
          <h2 className="text-2xl  font-bold text-center mb-6">
            Thrivebot Impact
          </h2>
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statsItems.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-md p-4 text-center h-24 flex flex-col justify-center"
                >
                  <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </h3>
                  <p className="text-xl font-bold mt-1 flex items-center justify-center">
                    {formatNumber(Number(stat.value))}
                    {stat.icon && (
                      <img
                        src={stat.icon}
                        alt="cents"
                        className="w-4 h-4 ml-1"
                      />
                    )}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center">Loading stats...</p>
          )}
        </motion.div> */}

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-center mb-6">
            Community Voices
          </h2>
          {/* <div className={`py-4 ${isLargeScreen ? "overflow-visible" : "overflow-hidden"}`}> */}
          <div className="py-4 overflow-hidden">
            <motion.div
              className="flex gap-6"
              animate={{ x: [0, -reviews.length * 300] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            >
              {[...reviews, ...reviews].map((review, index) => (
                <div
                  key={`${review.username}-${index}`}
                  className="bg-white dark:bg-gray-900 rounded-lg p-5 w-72 flex-shrink-0 shadow-md border border-gray-200 dark:border-gray-800 cursor-pointer"
                  onClick={() => handleReviewClick(review)}
                >
                  <div className="flex items-center mb-3">
                    <img
                      src={review.avatar}
                      alt={review.username}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <span className="ml-2 font-semibold text-sm">
                      {review.username}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-xs line-clamp-2">
                    {review.comment}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {selectedReview && (
        <Modal onClose={closeModal}>
          <div className="flex items-center mb-4">
            <img
              src={selectedReview.avatar}
              alt={selectedReview.username}
              className="w-14 h-14 rounded-full mr-3"
              loading="lazy"
            />
            <h2 className="text-xl font-bold">{selectedReview.username}</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-base max-w-md">
            {selectedReview.comment}
          </p>
        </Modal>
      )}

      <div className="fixed bottom-4 left-4 right-4 z-50 space-y-2">
        {activeInfoLines.map((line) => (
          <div
            key={line.id}
            className="flex items-center justify-between bg-blue-600 text-white p-3 rounded-lg shadow-lg"
          >
            <span className="flex-1 text-xs">
              {line.message}{" "}
              <strong>{new Date(line.endDate).toLocaleDateString()}</strong>
            </span>
            <button onClick={() => removeInfoLine(line.id)} className="ml-3">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Homepage;